import type { AppRouteModule } from '/@/router/types';

import { LAYOUT } from '/@/router/constant';
import { t } from '@mcex/locale';
import { PageEnum } from '/@/enums/pageEnum';
import { useProductStore } from '/@/store/modules/exchangeService/product';
import { MenuCodeEnum } from '/@/enums/menuCodeEnum';
import { OperationType, useTradEtpStoreWithOut } from '/@/store/modules/exchangeService/tradeEtp';
import { EtpDetailType } from '/@/api/exchangeService/model/productEtp';

const setup: AppRouteModule[] = [
  // 交易模块
  {
    path: '/transactions',
    name: 'Transaction',
    component: LAYOUT,
    redirect: PageEnum.TRANSACTION + '/:pType?',
    meta: {
      code: 'trading',
      orderNo: 2,
      hideChildrenInMenu: true,
      icon: 'menus-transaction|svg',
      title: t('routes.mcex.transaction'),
      menuCodes: [MenuCodeEnum.trading],
    },
    children: [
      {
        path: 'transaction/:pType?',
        props: true,
        name: 'Transaction',
        component: () => import('/@/views/exchangeService/transaction/index.vue'),
        meta: {
          title: t('routes.mcex.transaction'),
        },
        beforeEnter: (to, _from, next) => {
          const productType = to.params.pType;
          // productType 为 :productType时 修复地址移除:pType
          if (productType === ':pType') {
            next({ path: PageEnum.TRANSACTION });
          } else {
            next();
          }
        },
        children: [
          {
            path: 'detail/:productCode/:viewType',
            name: 'detail',
            component: () => import('/@/views/exchangeService/detail/index.vue'),
            meta: {
              title: t('routes.mcex.detail'),
              currentActiveMenu: PageEnum.TRANSACTION,
              menuCodes: [MenuCodeEnum.tradingDRODetail],
            },
            beforeEnter: (to) => {
              const productStore = useProductStore();
              productStore.fetchProductData(to.params.productCode as string);
              return true;
            },
          },
          {
            path: 'buy',
            name: 'TransfromBuying',
            component: () => import('/@/views/exchangeService/buying/buy.vue'),
            meta: {
              title: t('routes.mcex.purchase'),
              hideMenu: true,
              currentActiveMenu: PageEnum.TRANSACTION,
              menuCodes: [MenuCodeEnum.tradingAOPurchase, MenuCodeEnum.tradingSOPurchase],
            },
          },
          {
            path: 'detail_etp/:productCode/:pageType/:drpType?',
            name: 'DetailEtp',
            component: () => import('/@/views/exchangeService/detail_etp/index.vue'),
            meta: {
              title: t('routes.mcex.drpDetail'),
              currentActiveMenu: PageEnum.TRANSACTION,
              menuCodes: [MenuCodeEnum.tradingDRODetail],
            },
            beforeEnter: async (to) => {
              const tradEtpStore = useTradEtpStoreWithOut();
              tradEtpStore.setOperartType(to.params.pageType as OperationType);
              tradEtpStore.setEtpInfo({
                ...tradEtpStore.etpInfo,
                productCode: to.params.productCode as string,
              } as EtpDetailType);
              tradEtpStore.fetchEtpData(
                to.params.productCode as string,
                to.params.drpType as string,
              );
              return true;
            },
            children: [
              {
                path: 'asset_detail',
                name: 'AssetDetail',
                component: () => import('/@/views/exchangeService/asset_detail/index.vue'),
                meta: {
                  title: t('资产明细'),
                  currentActiveMenu: PageEnum.TRANSACTION,
                  // menuCodes: [MenuCodeEnum.assetDetail],
                },
              },
            ],
          },
          {
            path: 'dro_detail/:droCode/:listed?',
            name: 'droDetailL2',
            component: () => import('/@/views/market/dro/index.vue'),
            meta: {
              // affix: true,
              title: t('routes.mcex.droDetail'),
              hideBreadcrumb: false,
              currentActiveMenu: PageEnum.TRANSACTION,
            },
          },
        ],
      },
      {
        path: 'allDro/:brandId?',
        props: true,
        name: 'tradingAllDroPage',
        component: () => import('/@/views/exchangeService/transaction/AllDro.vue'),
        meta: {
          // affix: true,
          title: t('maap.allDro'),
          hideBreadcrumb: false,
          currentActiveMenu: PageEnum.TRANSACTION,
        },
      },
    ],
  },
  // 自选模块
  {
    path: '/optionals',
    name: 'Optionals',
    component: LAYOUT,
    redirect: PageEnum.OPTIONAL,
    meta: {
      orderNo: 2,
      hideChildrenInMenu: true,
      icon: 'menus-optional|svg',
      title: t('routes.mcex.optional'),
      menuCodes: [MenuCodeEnum.option],
    },
    children: [
      {
        path: 'optional',
        name: 'Optional',
        component: () => import('/@/views/exchangeService/optional/index.vue'),
        meta: {
          title: t('routes.mcex.optional'),
        },
        children: [
          {
            path: 'detail/:droCode/:listed',
            name: 'optionalDetail',
            component: () => import('/@/views/market/dro/index.vue'),
            meta: {
              title: t('routes.mcex.detail'),
              currentActiveMenu: PageEnum.OPTIONAL,
              menuCodes: [MenuCodeEnum.optionDRODetail],
            },
          },
          {
            path: 'buy',
            name: 'Buying',
            component: () => import('/@/views/exchangeService/buying/buy.vue'),
            meta: {
              title: t('routes.mcex.purchase'),
              hideMenu: true,
              currentActiveMenu: PageEnum.OPTIONAL,
              menuCodes: [MenuCodeEnum.optionAOPurchase, MenuCodeEnum.optionSOPurchase],
            },
          },
          {
            path: 'detail_etp/:productCode/:pageType/:drpType?',
            name: 'optionalDetailEtp',
            component: () => import('/@/views/exchangeService/detail_etp/index.vue'),
            meta: {
              title: t('routes.mcex.drpDetail'),
              currentActiveMenu: PageEnum.OPTIONAL,
              menuCodes: [MenuCodeEnum.optionDRODetail],
            },
            beforeEnter: async (to) => {
              const tradEtpStore = useTradEtpStoreWithOut();
              tradEtpStore.setOperartType(to.params.pageType as OperationType);
              tradEtpStore.setEtpInfo({
                ...tradEtpStore.etpInfo,
                productCode: to.params.productCode as string,
              } as EtpDetailType);
              tradEtpStore.fetchEtpData(
                to.params.productCode as string,
                to.params.drpType as string,
              );
              return true;
            },
          },
        ],
      },
    ],
  },
  // 持仓模块
  {
    path: '/holdings',
    name: 'Holdings',
    component: LAYOUT,
    redirect: PageEnum.HOLDING,
    meta: {
      orderNo: 2,
      hideChildrenInMenu: true,
      icon: 'menus-holding|svg',
      title: t('routes.mcex.holding'),
      menuCodes: [MenuCodeEnum.position],
    },
    children: [
      {
        path: 'holding',
        name: 'Holding',
        component: () => import('/@/views/exchangeService/holding/index.vue'),
        meta: {
          title: t('routes.mcex.holding'),
        },
        children: [
          {
            path: 'transfer',
            name: 'Transfer',
            component: () => import('/@/views/exchangeService/transfer/index.vue'),
            meta: {
              title: t('routes.mcex.transfer'),
              hideMenu: true,
              currentActiveMenu: PageEnum.HOLDING,
            },
          },
          {
            path: 'income/:tradingAccountCode',
            name: 'Income',
            component: () => import('/@/views/exchangeService/holding/income/index.vue'),
            meta: {
              title: t('routes.mcex.incomeDistribution'),
              hideMenu: true,
              currentActiveMenu: PageEnum.HOLDING,
              menuCodes: [MenuCodeEnum.positionIncome],
            },
          },
          {
            path: 'income-detail/:certificateAccount/:date',
            name: 'IncomeDetail',
            component: () => import('/@/views/exchangeService/holding/incomeDetail/index.vue'),
            meta: {
              title: t('routes.mcex.distributionDetail'),
              hideMenu: true,
              currentActiveMenu: PageEnum.HOLDING,
              menuCodes: [MenuCodeEnum.positionIncome],
            },
          },
          {
            path: 'detail_etp/:productCode/:pageType',
            name: 'holdingDetailEtp',
            component: () => import('/@/views/exchangeService/detail_etp/index.vue'),
            meta: {
              title: t('routes.mcex.drpDetail'),
              currentActiveMenu: PageEnum.HOLDING,
            },
            beforeEnter: async (to) => {
              const tradEtpStore = useTradEtpStoreWithOut();
              tradEtpStore.setOperartType(to.params.pageType as OperationType);
              tradEtpStore.setEtpInfo({
                ...tradEtpStore.etpInfo,
                productCode: to.params.productCode as string,
              } as EtpDetailType);
              tradEtpStore.fetchEtpData(
                to.params.productCode as string,
                to.params.drpType as string,
              );
              return true;
            },
          },
          {
            path: 'transfer_etp',
            name: 'TransferEtp',
            component: () => import('/@/views/exchangeService/buying_etp/buy.vue'),
            meta: {
              title: t('mcex.holding.listingTransfer'),
              hideMenu: true,
              currentActiveMenu: PageEnum.HOLDING,
            },
          },
        ],
      },
    ],
  },
  // 资金模块
  {
    path: '/finances',
    name: 'Finances',
    component: LAYOUT,
    redirect: PageEnum.FINANCE,
    meta: {
      orderNo: 2,
      hideChildrenInMenu: true,
      icon: 'menus-finance|svg',
      title: t('routes.mcex.finance'),
      menuCodes: [MenuCodeEnum.fund],
    },
    children: [
      {
        path: 'finance',
        name: 'Finance',
        component: () => import('/@/views/exchangeService/finance/index.vue'),
        meta: {
          title: t('routes.mcex.finance'),
          hideChildrenInMenu: true,
        },
        children: [
          {
            path: 'allocation/:fundAccountCode',
            name: 'Allocation',
            component: () => import('/@/views/exchangeService/finance/allocation/index.vue'),
            meta: {
              title: t('routes.mcex.transactionCapital'),
              hideMenu: true,
              currentActiveMenu: PageEnum.FINANCE,
              menuCodes: [MenuCodeEnum.fundTransfer],
            },
          },
          {
            path: 'detail',
            name: 'AllocationDetail',
            component: () =>
              import('/@/views/exchangeService/finance/allocation/AllocationDetail.vue'),
            meta: {
              title: t('routes.mcex.distributionRecord'),
              hideMenu: true,
              currentActiveMenu: PageEnum.FINANCE,
              menuCodes: [MenuCodeEnum.fundTransfer],
            },
          },
        ],
      },
    ],
  },

  // 委托模块
  {
    path: '/entrusts',
    name: 'Entrusts',
    component: LAYOUT,
    redirect: PageEnum.ENTRUST_DETAIL,
    meta: {
      orderNo: 2,
      hideChildrenInMenu: true,
      icon: 'menus-entrust|svg',
      title: t('routes.mcex.entrust'),
      menuCodes: [MenuCodeEnum.agency],
    },
    children: [
      {
        path: 'detail/:productType?/:droType?',
        name: 'EntrustDetail',
        component: () => import('/@/views/exchangeService/entrust/EntrustDetail.vue'),
        meta: {
          title: t('mcex.entrust.pageTitle'),
          currentActiveMenu: PageEnum.ENTRUST_DETAIL,
          hideMenu: true,
        },
      },
    ],
  },

  // 结单模块
  {
    path: '/settle',
    name: 'Settle',
    component: LAYOUT,
    redirect: PageEnum.SETTLE_LIST,
    meta: {
      orderNo: 2,
      hideChildrenInMenu: true,
      icon: 'menus-settle|svg',
      title: t('routes.mcex.settle'),
      menuCodes: [MenuCodeEnum.settlement],
    },
    children: [
      {
        path: 'list',
        name: 'SettleList',
        component: () => import('/@/views/exchangeService/settle/index.vue'),
        meta: {
          title: t('routes.mcex.settle'),
          hideChildrenInMenu: true,
        },
      },
    ],
  },
];

export default setup;
